import { useEffect } from "react";
import gql from "graphql-tag";
import { useQuery } from "@apollo/react-hooks";
import { gqlType } from "@hifieng/common";

import { ApolloError } from "apollo-boost";
import { logErrorToLogdna } from "../../helpers/logdna";
import { useOrganizationContext } from "../../contexts/OrganizationProvider";
import {
  trainRunFragment,
  trainUpdatesFragment,
  ACTIVE_TRAINS_SUBSCRIPTION
} from "../../helpers/trainQueryComposer";

export const TRAIN_DETAILS = gql`
  query trainRun($id: String!) {
    trainRun(id: $id) {
      ...TrainRunFragment
      startLocation {
        latitude
        longitude
      }
      endLocation {
        latitude
        longitude
      }
      startPost
      endPost
      updates {
        ...TrainUpdateFragment
      }
      auditLogs {
        id
        trainId
        createdAt
        comment {
          author
          message
        }
      }
    }
  }
  ${trainRunFragment}
  ${trainUpdatesFragment}
`;

export const useTrainDetails = (id: string, subscribeToChange?: boolean) => {
  const { activeOrg } = useOrganizationContext();

  const { subscribeToMore, loading, error, data, refetch } = useQuery<{
    trainRun: gqlType.TrainRun;
  }>(TRAIN_DETAILS, {
    variables: { id },
    onError: (err: ApolloError) => {
      logErrorToLogdna(
        "GETTING_TRAIN_DETAILS",
        "Apollo error while getting train run details.",
        err
      );
      return err;
    }
  });

  useEffect(() => {
    if (!activeOrg) {
      return;
    }
    if (subscribeToChange) {
      subscribeToMore({
        document: ACTIVE_TRAINS_SUBSCRIPTION,
        variables: {
          organizationId: activeOrg.id
        },
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData.data) {
            return prev;
          } else {
            // These types appear incorrect, so coerce them into the TrainRun type
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const subscribed: gqlType.TrainRun = (subscriptionData.data as any)
              .trackTrain;

            // If two train updates arrive at the same time, the "latest" fetched value will be the
            // more recent of the two, resulting in the same update being processed twice.
            // This code ignores out any updates that already exist.
            const updateAlreadyExists =
              prev.trainRun.updates.findIndex(
                existingUpdate => existingUpdate.id === subscribed.latest.id
              ) !== -1;

            if (
              prev &&
              prev.trainRun.id === subscribed.id &&
              !updateAlreadyExists
            ) {
              return Object.assign({}, prev, {
                trainRun: {
                  ...prev.trainRun,
                  ...subscribed,
                  updates: [subscribed.latest, ...prev.trainRun.updates]
                }
              });
            }
          }
          return prev;
        }
      });
    }

    //suppressing hook dependency because `subscribeToMore` changes on every render
    // and the documentation suggest to only call it on component mount
    // https://www.apollographql.com/docs/react/data/subscriptions/#subscribing-to-updates-for-a-query
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrg]);

  return {
    error,
    status: loading && !data ? "loading" : "connected",
    trainData: !loading && data ? data : undefined,
    refetch
  };
};
